<template>
  <UCard
    :ui="{
      strategy: 'override',
      background: selected ? 'bg-trublue-50' : 'bg-white',
      ring: '',
      shadow: '',
      header: {
        padding: 'pl-8 pr-6 py-4 sm:pl-8 sm:pr-6'
      },
      body: {
        padding: 'pl-8 pr-6 py-0 sm:py-0 sm:pl-8 sm:pr-6'
      },
      footer: {
        padding: 'pl-8 pr-6 py-4 sm:pl-8 sm:pr-6 pb-2',
        base: 'border-b-2 border-gray-100'
      },
      divide: ''
    }"
  >
    <template #header>
      <div class="w-full">
        <h3 class="w-[90%] font-semibold whitespace-normal text-darkPurple">
          {{ result.displayName }}
        </h3>

        <div class="flex items-center justify-between mt-2 text-sm gap-x-2">
          <div class="w-[90%] text-gray-700 whitespace-normal">
            <p>{{ addressConcatenated }}</p>
          </div>

          <div
            v-if="showDistance"
            class="flex items-center self-start justify-end w-24 text-sm font-semibold shrink-0 text-darkPurple gap-x-1"
          >
            <img src="@/assets/images/map-pin.svg" alt="Map Pin" class="w-5 h-5" />
            <span class="text-darkPurple">{{ distanceUtil.formatDistance(distance) }}</span>
          </div>
        </div>
      </div>
    </template>

    <div class="flex items-start justify-between w-full mt-2">
      <UiStarRatings
        v-if="result.truRating && result.ratingCount"
        :rating="parseFloat(result.truRating)"
        :total-ratings="result.ratingCount"
        variant="purple"
      />

      <div v-if="smallViewport" @click="() => onStoreClicked(result.id)" class="flex cursor-pointer">
        <div class="text-sm font-bold text-trublue-700">
          {{ t('showOnMap') }}
        </div>
        <div class="pl-1 -mt-[1px]">
          <font-awesome-icon icon="fa-solid fa-chevron-right" class="text-trublue-700" />
        </div>
      </div>
    </div>

    <template #footer>
      <div v-if="result.isProfileEnabled" class="flex justify-between md:w-[300px] sm:w-full pr-2">
        <div class="flex items-center space-x-2 text-xs text-gray-600">
          <span v-for="badgeText in result.badges">{{ badgeText }}</span>
        </div>
        <UButton
          v-if="result.profileUrl"
          :to="result.profileUrl"
          target="_blank"
          variant="outline"
          size="xs"
          :ui="{
            rounded: 'rounded-2xl',
            font: 'font-bold'
          }"
          class="text-trublue-700 border-trublue-700 shrink-0 ml-auto max-w-[120px] truncate"
        >
          {{ t('more') }}
          <template #trailing>
            <font-awesome-icon icon="fa-solid fa-chevron-right" class="text-trublue-700" />
          </template>
        </UButton>
      </div>
    </template>
  </UCard>
</template>

<script setup lang="ts">
import type { StoreResult } from '@/types'
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core'

const breakpoints = useBreakpoints(breakpointsTailwind)
const activeBreakpoints = breakpoints.active()
const smallViewport = computed(() => !activeBreakpoints.value || ['sm'].includes(activeBreakpoints.value))
const { t } = useI18n()
const props = defineProps<{ result: StoreResult; showDistance: boolean; selected: boolean }>()
const ui = useUiStore()
const map = useMapStore()
const gtm = useGtm()
const search = useSearchStore()
const distanceUtil = useDistanceStore()

const { town, county, postCode, distance } = props.result
const addressConcatenated = [town, county, postCode].filter(Boolean).join(', ')
const distanceText = computed(() => {
  if (isNaN(distance) || distance === undefined) return ''
  return distance < 10 ? n(distance, 'decimal') : distance.toFixed(0)
})

function onStoreClicked(storeId: number) {
  const store = search.getStoreById(storeId)
  map.selectedFeatureId = store.id
  ui.showMapView()

  if (!smallViewport.value) {
    map.navigateTo = [store.long, store.lat]

    nextTick(() => {
      const feature = (map as any).getFeatureById?.(store.id)
      const coordinate = feature?.getGeometry?.().getCoordinates?.()
      if (coordinate) {
        ;(map as any).setPopoverPosition?.(coordinate)
      }
    })
  }

  gtm?.trackEvent({
    event: 'Store List Selected',
    category: 'discovery',
    action: 'User selected store from Store List',
    label: store.displayName
  })
}
</script>
